import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { Button } from '@/components/ui/button';
import { notificationsService } from '@/services/notifications.service';

function formatRelativeTime(isoStr: string): string {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function NotificationsWidget() {
  const qc = useQueryClient();

  const notificationsQ = useQuery({
    queryKey: ['dashboard', 'notifications'],
    queryFn: () => notificationsService.list({ page: 1, limit: 5 }),
  });

  const markAllMutation = useMutation({
    mutationFn: () => notificationsService.markAllRead(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dashboard', 'notifications'] });
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const items = notificationsQ.data?.data ?? [];
  const unread = items.filter((n) => !n.read_at);

  return (
    <Card className="shadow-card">
      <SectionHeader
        icon={<Bell />}
        title="Notifications"
        action={
          unread.length > 0 ? (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-xs"
              onClick={() => markAllMutation.mutate()}
              disabled={markAllMutation.isPending}
            >
              Clear all
            </Button>
          ) : undefined
        }
      />
      <CardContent className="p-0">
        {notificationsQ.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-muted/50" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <Bell className="h-8 w-8 opacity-30" />
            <p className="text-sm text-muted-foreground">You're all caught up</p>
          </div>
        ) : (
          <div className="divide-y divide-border/40">
            {items.map((n) => (
              <div
                key={n.id}
                className={`flex items-start gap-3 px-4 py-3 transition-colors ${
                  !n.read_at ? 'bg-primary/3' : ''
                }`}
              >
                <div
                  className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full transition-colors ${
                    !n.read_at ? 'bg-teal-500' : 'bg-muted-foreground/30'
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-2 text-sm font-medium leading-snug">{n.title}</p>
                  {n.body && (
                    <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{n.body}</p>
                  )}
                  <p className="mt-0.5 text-[11px] text-muted-foreground/70">
                    {formatRelativeTime(n.created_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
